import React, { useEffect, useState } from "react";
import { Book, Loader2, ArrowUpRight, ArrowDownLeft, AlertTriangle, Leaf } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatGBP } from "@/lib/marketplace";
import { subscribeWalletUpdates } from "@/lib/walletBus";
import TransferStatusPill from "@/components/TransferStatusPill";

// Iteration 283 — pair a passbook cash-out row with its cash-out
// request (amount + calendar day of the decision) so the payout badge
// ("Paid in Cash" / "Paid to Bank" / "Held by Parent") can render
// beside the transfer in the kid's own passbook.
const matchCashout = (t, absPence, cashouts) => {
  const day = (t.created_at || "").slice(0, 10);
  return (cashouts || []).find(
    (c) => c.status === "approved" && (c.amount_pennies || 0) === absPence
      && ((c.decided_at || c.created_at || "").slice(0, 10) === day),
  ) || null;
};

/**
 * ChildLedger — reusable passbook / transaction history component.
 *
 * Props:
 *   • endpoint     — e.g. "/kid/ledger" or "/kids/{id}/ledger". Required.
 *   • title        — heading (default "Passbook history").
 *   • limit        — how many rows to fetch (default 25).
 *   • testIdPrefix — prefix for data-testids (default "child-ledger").
 *
 * Renders a clean table with date / description / reference / +£/-£ so
 * families see every credit and debit at a glance. Empty histories render
 * a friendly empty state — the passbook only ever shows the account's own
 * real transactions.
 */
export default function ChildLedger({
  endpoint,
  title = "Passbook history",
  limit = 25,
  testIdPrefix = "child-ledger",
}) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [txns, setTxns] = useState([]);
  // Iteration 283 — payout badges on the kid's OWN passbook. Other
  // contexts (parent viewing a kid's passbook) can't call the kid
  // endpoint, so this fails open with no badges.
  const [cashouts, setCashouts] = useState([]);
  // Iteration 292 — "Landfill Waste Diverted" counter from the passbook feed.
  const [weightDiverted, setWeightDiverted] = useState(null);

  useEffect(() => {
    if (endpoint !== "/kid/ledger") return undefined;
    let cancel = false;
    api.get("/kid/wallet/cashout-requests")
      .then(({ data }) => { if (!cancel) setCashouts(data?.requests || []); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [endpoint]);

  useEffect(() => {
    if (!endpoint) return;
    let cancelled = false;
    const reload = (showSpinner = true) => {
      if (showSpinner) setLoading(true);
      setErr("");
      api.get(endpoint, { params: { limit } }).then(({ data }) => {
        if (cancelled) return;
        setTxns(data?.transactions || []);
        setWeightDiverted(typeof data?.weight_diverted_kg === "number" ? data.weight_diverted_kg : null);
      }).catch((e) => {
        if (cancelled) return;
        const raw = formatApiError(e);
        setErr(raw ? `Couldn't load passbook — ${raw}` : "Couldn't load passbook. Please try again.");
      }).finally(() => { if (!cancelled) setLoading(false); });
    };
    reload();
    // Iteration 87 — subscribe to wallet events so a completed sale or
    // fresh parent deposit immediately shows up as a new passbook row
    // with the updated running balance, no manual refresh needed.
    const unsub = subscribeWalletUpdates(() => reload(false));
    return () => { cancelled = true; unsub?.(); };
  }, [endpoint, limit]);

  return (
    <div className="card-base p-5 md:p-6 mt-6" data-testid={testIdPrefix}>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-brandTeal text-white shadow-sm">
            <Book className="w-4 h-4" />
          </div>
          <div>
            <h3 className="font-display text-lg md:text-xl font-semibold text-brandSlate leading-tight">{title}</h3>
            <p className="text-xs text-slate-500">Every credit &amp; debit, newest first.</p>
          </div>
        </div>
        {typeof weightDiverted === "number" && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid={`${testIdPrefix}-weight-diverted`}>
            <Leaf className="w-3 h-3" /> Landfill Waste Diverted: {weightDiverted.toFixed(1)} kg
          </span>
        )}
      </div>

      {loading ? (
        <div className="rounded-2xl bg-slate-50 border border-slate-100 px-4 py-6 text-center text-sm text-slate-500 flex items-center justify-center gap-2" data-testid={`${testIdPrefix}-loading`}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading&hellip;
        </div>
      ) : err ? (
        <div className="rounded-2xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-800 flex items-center gap-2" data-testid={`${testIdPrefix}-error`}>
          <AlertTriangle className="w-4 h-4" /> {err}
        </div>
      ) : txns.length === 0 ? (
        <div className="rounded-2xl bg-slate-50 border border-slate-100 px-4 py-8 text-center" data-testid={`${testIdPrefix}-empty`}>
          <Book className="w-6 h-6 mx-auto text-slate-400" />
          <div className="font-bold text-slate-700 mt-2">No transactions yet!</div>
          <p className="text-xs text-slate-500 mt-1">Sold items and allowance will show up here.</p>
        </div>
      ) : (
        <>
          {/* Desktop: table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm" data-testid={`${testIdPrefix}-table`}>
              <thead>
                <tr className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 text-left">
                  <th className="pb-3">Date</th>
                  <th className="pb-3">Description</th>
                  <th className="pb-3">Direction</th>
                  <th className="pb-3 text-right">Amount</th>
                  <th className="pb-3 text-right">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {txns.map((t) => (
                  <LedgerRow key={t.id} t={t} testIdPrefix={testIdPrefix} variant="table" cashouts={cashouts} />
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile: card stack */}
          <div className="md:hidden space-y-2" data-testid={`${testIdPrefix}-cards`}>
            {txns.map((t) => (
              <LedgerRow key={t.id} t={t} testIdPrefix={`${testIdPrefix}-mobile`} variant="card" cashouts={cashouts} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function LedgerRow({ t, testIdPrefix, variant, cashouts = [] }) {
  // Iteration 111 — prefer the new canonical signed `delta_pennies` when
  // upstream emits it (matches mobile behaviour, avoids sign inference).
  // Falls back to the legacy `direction`/`amount_pennies` pair for rows
  // that haven't been re-hydrated yet.
  const hasDelta = t.delta_pennies !== undefined && t.delta_pennies !== null;
  const deltaSigned = hasDelta ? Number(t.delta_pennies) : null;
  const positive = hasDelta
    ? deltaSigned >= 0
    : t.direction === "credit";
  const absPence = hasDelta
    ? Math.abs(deltaSigned)
    : Number(t.amount_pennies || 0);
  const sign = positive ? "+" : "-";
  const amountColor = positive ? "text-emerald-700" : "text-rose-700";
  const Icon = positive ? ArrowDownLeft : ArrowUpRight;
  const bg = positive ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700";
  const date = t.created_at ? new Date(t.created_at) : null;
  const dateStr = date ? date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
  // Iteration 278 — cash-out transfers to the parent household account
  // fall through the backend's unknown-kind fallback and arrive as
  // "Me → Other". Re-label any outgoing-to-parent row as "Me → Parent".
  const kindLc = (t.kind || t.type || "").toString().toLowerCase();
  const CASHOUT_KINDS = new Set([
    "transfer_to_parent", "kid_to_parent", "sent_to_parent",
    "cashout", "cash_out", "cashout_to_parent", "wallet_transfer_to_parent",
  ]);
  const directionBadge = !positive && (CASHOUT_KINDS.has(kindLc) || t.direction_badge === "Me → Other")
    ? "Me → Parent"
    : t.direction_badge;
  // Iteration 283 — cash-out passbook rows carry the payout status badge
  // (synced from the kid's cash-out requests feed, matched by amount+day).
  const isCashoutRow = !positive
    && (CASHOUT_KINDS.has(kindLc) || t.kind_group === "to_parent" || directionBadge === "Me → Parent");
  const cashoutMatch = isCashoutRow ? matchCashout(t, absPence, cashouts) : null;

  if (variant === "card") {
    return (
      <div className="rounded-2xl border border-slate-100 bg-white px-3 py-2.5 flex items-center gap-3" data-testid={`${testIdPrefix}-row-${t.id}`}>
        <div className={`w-9 h-9 rounded-full inline-flex items-center justify-center flex-shrink-0 ${bg}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-sm text-slate-900 truncate" data-testid={`${testIdPrefix}-desc-${t.id}`}>
            {t.description}
          </div>
          <div className="text-[11px] text-slate-500 truncate">
            {dateStr}
            {directionBadge && <span className="ml-1 text-slate-700 font-semibold" data-testid={`${testIdPrefix}-badge-${t.id}`}> · {directionBadge}</span>}
          </div>
          {(t.sponsored_purchase || t.csr_gifted) && (
            <div className="mt-1 flex flex-wrap gap-1">
              {t.sponsored_purchase && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border bg-amber-100 text-amber-900 border-amber-200"
                  data-testid={`${testIdPrefix}-sponsored-${t.id}`}
                >
                  🌟 Bought by Community Sponsorship Purchase Fund (+{formatGBP(absPence / 100)})
                </span>
              )}
              {t.csr_gifted && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border bg-teal-100 text-teal-900 border-teal-200"
                  data-testid={`${testIdPrefix}-csr-${t.id}`}
                >
                  🤝 CSR (Corporate Social Responsibility) Gifted Item
                </span>
              )}
            </div>
          )}
          {cashoutMatch && (
            <div className="mt-1">
              <TransferStatusPill row={cashoutMatch} testid={`${testIdPrefix}-payout-${t.id}`} />
            </div>
          )}
        </div>
        <div className="flex flex-col items-end flex-shrink-0">
          <div className={`font-mono font-bold text-sm ${amountColor}`} data-testid={`${testIdPrefix}-amount-${t.id}`}>
            {sign}{formatGBP(absPence / 100)}
          </div>
          {typeof t.running_balance_pennies === "number" && (
            <div className="font-mono text-[10px] text-slate-500" data-testid={`${testIdPrefix}-balance-${t.id}`}>
              bal {formatGBP(t.running_balance_pennies / 100)}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <tr data-testid={`${testIdPrefix}-row-${t.id}`}>
      <td className="py-3 text-slate-500 whitespace-nowrap">{dateStr}</td>
      <td className="py-3">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full inline-flex items-center justify-center flex-shrink-0 ${bg}`}>
            <Icon className="w-3.5 h-3.5" />
          </div>
          <span className="font-semibold text-slate-900" data-testid={`${testIdPrefix}-desc-${t.id}`}>{t.description}</span>
        </div>
        {(t.sponsored_purchase || t.csr_gifted) && (
          <div className="mt-1 flex flex-wrap gap-1">
            {t.sponsored_purchase && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border bg-amber-100 text-amber-900 border-amber-200"
                data-testid={`${testIdPrefix}-sponsored-${t.id}`}
              >
                🌟 Bought by Community Sponsorship Purchase Fund (+{formatGBP(absPence / 100)})
              </span>
            )}
            {t.csr_gifted && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border bg-teal-100 text-teal-900 border-teal-200"
                data-testid={`${testIdPrefix}-csr-${t.id}`}
              >
                🤝 CSR (Corporate Social Responsibility) Gifted Item
              </span>
            )}
          </div>
        )}
      </td>
      <td className="py-3">
        {directionBadge ? (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border ${positive ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-rose-50 text-rose-800 border-rose-200"}`}
            data-testid={`${testIdPrefix}-badge-${t.id}`}
          >
            {directionBadge}
          </span>
        ) : (
          <span className="font-mono text-[11px] text-slate-500" data-testid={`${testIdPrefix}-ref-${t.id}`}>{t.ref || "—"}</span>
        )}
        {cashoutMatch && (
          <div className="mt-1">
            <TransferStatusPill row={cashoutMatch} testid={`${testIdPrefix}-payout-${t.id}`} />
          </div>
        )}
      </td>
      <td className={`py-3 text-right font-mono font-bold ${amountColor}`} data-testid={`${testIdPrefix}-amount-${t.id}`}>
        {sign}{formatGBP(absPence / 100)}
      </td>
      <td className="py-3 text-right font-mono text-slate-800 whitespace-nowrap" data-testid={`${testIdPrefix}-balance-${t.id}`}>
        {typeof t.running_balance_pennies === "number" ? formatGBP(t.running_balance_pennies / 100) : <span className="text-slate-400">—</span>}
      </td>
    </tr>
  );
}
